import { useId } from "react";

import { Alert, CircularProgress, Stack, Typography } from "@mui/material";

import { CourtHearingResponse } from "@/api";
import { CourtHearingsGrouping } from "@/pages/court-hearings/CourtHearings.types";
import {
  CourtGroup,
  CourtInfo,
  DayGroup,
  groupByCourtThenDate,
  groupByDateThenCourt,
} from "@/pages/court-hearings/CourtHearings.utils";
import { useTranslation } from "@/translations";

import { CourtGroupHeading } from "./components/court-heading/CourtGroupHeading";
import { DayGroupHeading } from "./components/day-heading/DayGroupHeading";
import { CourtHearingsTable } from "./components/hearings-table/CourtHearingsTable";

interface Props {
  rows: CourtHearingResponse[];
  grouping: CourtHearingsGrouping;
  isLoading: boolean;
  isError: boolean;
  /** False while the filter is invalid — nothing was asked for, so neither a result nor "empty" applies. */
  hasSearchParams: boolean;
}

export function CourtHearingsReport({ rows, grouping, isLoading, isError, hasSearchParams }: Props) {
  const { t } = useTranslation();

  if (isError) {
    return <Alert severity="error">{t("courtHearings.report.error")}</Alert>;
  }

  if (isLoading) {
    return (
      <Stack alignItems="center" py={6}>
        <CircularProgress aria-label={t("courtHearings.report.loading")} />
      </Stack>
    );
  }

  if (!hasSearchParams) {
    return null;
  }

  if (rows.length === 0) {
    return <Typography color="text.secondary">{t("courtHearings.report.empty")}</Typography>;
  }

  return grouping === "byDate" ? <HearingsByDate rows={rows} /> : <HearingsByCourt rows={rows} />;
}

function HearingsByDate({ rows }: { rows: CourtHearingResponse[] }) {
  return (
    <Stack spacing={4}>
      {groupByDateThenCourt(rows).map((day) => (
        <Stack key={day.key} spacing={2}>
          <DayGroupHeading date={day.date} level={2} />
          {day.courts.map((court) => (
            <CourtSection key={court.key} court={court} />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

function HearingsByCourt({ rows }: { rows: CourtHearingResponse[] }) {
  return (
    <Stack spacing={4}>
      {groupByCourtThenDate(rows).map((court) => (
        <Stack key={court.key} spacing={2}>
          <CourtGroupHeading court={court} level={2} />
          {court.days.map((day) => (
            <DaySection key={day.key} day={day} />
          ))}
        </Stack>
      ))}
    </Stack>
  );
}

function CourtSection({ court }: { court: CourtGroup }) {
  const headingId = useId();
  const info: CourtInfo = court;

  return (
    <Stack spacing={1}>
      <CourtGroupHeading court={info} level={3} id={headingId} />
      <CourtHearingsTable rows={court.rows} labelledById={headingId} />
    </Stack>
  );
}

function DaySection({ day }: { day: DayGroup }) {
  const headingId = useId();

  return (
    <Stack spacing={1}>
      <DayGroupHeading date={day.date} level={3} id={headingId} />
      <CourtHearingsTable rows={day.rows} labelledById={headingId} />
    </Stack>
  );
}
